'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReminderSettings } from '@/core/domain';
import {
  parsePreferredNotificationTime,
  parseReminderOffsetsDays,
} from '@/core/domain';
import type { GetUserUidUseCase } from '@/features/authentication/domain/usecases/get-user-uid';
import type { GetReminderSettingsByUserIdUseCase } from '../../domain/usecases/get-reminder-settings-by-user-id';
import type { CreateReminderSettingsUseCase } from '../../domain/usecases/create-reminder-settings';
import type { UpdateReminderSettingsUseCase } from '../../domain/usecases/update-reminder-settings';
import type { DeleteReminderSettingsUseCase } from '../../domain/usecases/delete-reminder-settings';
import type { SendWhatsAppTestMessageUseCase } from '../../domain/usecases/send-whatsapp-test-message.usecase';
import type { SubscribeMessageTestSendedUseCase } from '../../domain/usecases/subscribe-message-test-sended.usecase';
import type { SendWhatsAppTestMessageInput } from '@/core/services/whatsapp/whatsapp-functions.service';

export interface ReminderSettingsState {
  currentSettings: ReminderSettings | null;
  loading: boolean;
  error: string | null;
}

/** Resultado de operações que não retornam dados; usado para feedback (toast) na UI. Após sucesso, o refresh atualiza o estado. */
export interface ReminderSettingsOperationResult {
  success: boolean;
  error: string | null;
}

export interface ReminderSettingsEvents {
  /** Refresh: carrega configurações do usuário. Chamado na inicialização da tela e após create/update/delete. */
  getByUserId: () => Promise<ReminderSettings | null>;
  create: (data: Partial<ReminderSettings>) => Promise<ReminderSettingsOperationResult>;
  update: (data: Partial<ReminderSettings>) => Promise<ReminderSettingsOperationResult>;
  delete: () => Promise<ReminderSettingsOperationResult>;
  sendWhatsAppTestMessage: (
    input: SendWhatsAppTestMessageInput
  ) => Promise<ReminderSettingsOperationResult>;
  /**
   * Tempo real: `users/{uid}` + primeiro `reminder_settings` (inclui legado em `users`).
   * @return `null` se não autenticado; senão função para cancelar a inscrição.
   */
  subscribeMessageTestSendedFlag: (
    onNext: (sended: boolean) => void,
    onError?: (error: Error) => void
  ) => Promise<(() => void) | null>;
  clearError: () => void;
  clearCurrentSettings: () => void;
}

export interface ReminderSettingsContextValue {
  state: ReminderSettingsState;
  events: ReminderSettingsEvents;
}

export interface ReminderSettingsProviderProps {
  children: React.ReactNode;
  getUserUidUseCase: GetUserUidUseCase;
  getReminderSettingsByUserIdUseCase: GetReminderSettingsByUserIdUseCase;
  createReminderSettingsUseCase: CreateReminderSettingsUseCase;
  updateReminderSettingsUseCase: UpdateReminderSettingsUseCase;
  deleteReminderSettingsUseCase: DeleteReminderSettingsUseCase;
  sendWhatsAppTestMessageUseCase: SendWhatsAppTestMessageUseCase;
  subscribeMessageTestSendedUseCase: SubscribeMessageTestSendedUseCase;
}

const initialState: ReminderSettingsState = {
  currentSettings: null,
  loading: false,
  error: null,
};

const ReminderSettingsContext = createContext<ReminderSettingsContextValue | null>(null);

export function ReminderSettingsProvider({
  children,
  getUserUidUseCase,
  getReminderSettingsByUserIdUseCase,
  createReminderSettingsUseCase,
  updateReminderSettingsUseCase,
  deleteReminderSettingsUseCase,
  sendWhatsAppTestMessageUseCase,
  subscribeMessageTestSendedUseCase,
}: ReminderSettingsProviderProps) {
  const [state, setState] = useState<ReminderSettingsState>(initialState);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const clearCurrentSettings = useCallback(() => {
    setState((s) => ({ ...s, currentSettings: null }));
  }, []);

  const getUserUid = useCallback(async (): Promise<string | null> => {
    const userId = await getUserUidUseCase.execute();
    if (!userId) {
      setState((s) => ({ ...s, error: 'Usuário não autenticado.', currentSettings: null }));
      return null;
    }
    return userId;
  }, [getUserUidUseCase]);

  const getByUserId = useCallback(
    async (): Promise<ReminderSettings | null> => {
      const userId = await getUserUid();
      if (!userId) {
        setState((s) => ({ ...s, error: 'Usuário não autenticado.', currentSettings: null }));
        return null;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const settings = await getReminderSettingsByUserIdUseCase.execute(userId);
        setState((s) => ({ ...s, currentSettings: settings }));
        return settings;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: e instanceof Error ? e.message : String(e),
          currentSettings: null,
        }));
        return null;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, getReminderSettingsByUserIdUseCase]
  );

  const create = useCallback(
    async (data: Partial<ReminderSettings>): Promise<ReminderSettingsOperationResult> => {
      const userId = await getUserUid();
      if (!userId) {
        const err = 'Usuário não autenticado.';
        setState((s) => ({ ...s, error: err }));
        return { success: false, error: err };
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const now = new Date().toISOString();
        const full: ReminderSettings = {
          id: userId,
          user_id: userId,
          reminder_offsets_days: parseReminderOffsetsDays(data.reminder_offsets_days),
          preferred_notification_time: parsePreferredNotificationTime(data.preferred_notification_time),
          message_test_sended: data.message_test_sended ?? false,
          message_test_outbound_wamid: data.message_test_outbound_wamid ?? null,
          created_at: now,
          updated_at: now,
        };
        await createReminderSettingsUseCase.execute(userId, full);
        await getByUserId();
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage }));
        return { success: false, error: errorMessage };
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, createReminderSettingsUseCase, getByUserId]
  );

  const update = useCallback(
    async (data: Partial<ReminderSettings>): Promise<ReminderSettingsOperationResult> => {
      const userId = await getUserUid();
      if (!userId) {
        const err = 'Usuário não autenticado.';
        setState((s) => ({ ...s, error: err }));
        return { success: false, error: err };
      }
      const existing = state.currentSettings;
      if (!existing) {
        const err = 'Nenhuma configuração carregada para atualizar.';
        setState((s) => ({ ...s, error: err }));
        return { success: false, error: err };
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await updateReminderSettingsUseCase.execute(userId, existing.id, data);
        await getByUserId();
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage }));
        return { success: false, error: errorMessage };
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [updateReminderSettingsUseCase, state.currentSettings, getByUserId]
  );

  const deleteSettings = useCallback(
    async (): Promise<ReminderSettingsOperationResult> => {
      const userId = await getUserUid();
      if (!userId) {
        const err = 'Usuário não autenticado.';
        setState((s) => ({ ...s, error: err }));
        return { success: false, error: err };
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await deleteReminderSettingsUseCase.execute(userId, userId);
        await getByUserId();
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage }));
        return { success: false, error: errorMessage };
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [deleteReminderSettingsUseCase, getByUserId]
  );

  const sendWhatsAppTestMessage = useCallback(
    async (input: SendWhatsAppTestMessageInput): Promise<ReminderSettingsOperationResult> => {
      setState((s) => ({ ...s, error: null }));
      try {
        await sendWhatsAppTestMessageUseCase.execute(input);
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage }));
        return { success: false, error: errorMessage };
      }
    },
    [sendWhatsAppTestMessageUseCase]
  );

  const subscribeMessageTestSendedFlag = useCallback(
    async (
      onNext: (sended: boolean) => void,
      onError?: (error: Error) => void
    ): Promise<(() => void) | null> => {
      const userId = await getUserUid();
      if (!userId) return null;
      return subscribeMessageTestSendedUseCase.execute(userId, onNext, onError);
    },
    [getUserUid, subscribeMessageTestSendedUseCase]
  );

  const events: ReminderSettingsEvents = useMemo(
    () => ({
      getByUserId,
      create,
      update,
      delete: deleteSettings,
      sendWhatsAppTestMessage,
      subscribeMessageTestSendedFlag,
      clearError,
      clearCurrentSettings,
    }),
    [
      getByUserId,
      create,
      update,
      deleteSettings,
      sendWhatsAppTestMessage,
      subscribeMessageTestSendedFlag,
      clearError,
      clearCurrentSettings,
    ]
  );

  const value: ReminderSettingsContextValue = useMemo(
    () => ({ state, events }),
    [state, events]
  );

  return (
    <ReminderSettingsContext.Provider value={value}>
      {children}
    </ReminderSettingsContext.Provider>
  );
}

export function useReminderSettings(): ReminderSettingsContextValue {
  const ctx = useContext(ReminderSettingsContext);
  if (!ctx) {
    throw new Error('useReminderSettings must be used within ReminderSettingsProvider');
  }
  return ctx;
}
